/**
 * React Query hooks for 患者情報検索 / Search Patient List.
 * Search results + master data (insurer_types, sexes, dm_reason_types).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import {
  getSearchPatientListMasterData,
  searchPatientList,
} from '@/services/main/searchPatientList';
import {
  isServerError,
  showServerErrorModal,
} from '@/pages/main/searchPatientList/serverErrorModal';
import type {
  SearchPatientListMasterDataResponse,
  SearchPatientListParams,
  SearchPatientListResponse,
} from '@/pages/main/searchPatientList/types';
import { useClinicContext } from './useClinicContext';

/**
 * Paginated search. Pass null to skip (initial state before first submit).
 */
export function useSearchPatientList(params: SearchPatientListParams | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const appModal = useAppModal();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery<
    SearchPatientListResponse,
    Error
  >({
    queryKey: ['patients', 'search-patients', tenant, clinicNumber, params],
    queryFn: () => searchPatientList(tenant, clinicNumber, params!),
    enabled: !!tenant && !!clinicNumber && params !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (!isError || !error) return;
    if (isServerError(error)) {
      showServerErrorModal(appModal.warning);
      return;
    }
    toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
  }, [isError, error, toast, appModal]);

  const responseData = data?.data;
  const rows = responseData?.data ?? [];
  const pagination = responseData?.pagination ?? null;

  return {
    rows,
    pagination,
    isLoading: isLoading || isFetching,
    isError,
    isPending,
    refetch,
    error,
  };
}

/**
 * Master data for the search form. Rarely changes — cached 5 min.
 */
export function useSearchPatientListMasterData() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const appModal = useAppModal();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    SearchPatientListMasterDataResponse,
    Error
  >({
    queryKey: ['patients', 'search-patients', 'master-data', tenant, clinicNumber],
    queryFn: () => getSearchPatientListMasterData(tenant, clinicNumber),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isError || !error) return;
    if (isServerError(error)) {
      showServerErrorModal(appModal.warning);
      return;
    }
    toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
  }, [isError, error, toast, appModal]);

  return {
    insurerTypes: data?.data?.insurer_types ?? [],
    sexes: data?.data?.sexes ?? [],
    dmReasonTypes: data?.data?.dm_reason_types ?? [],
    isLoading,
    isError,
    isPending,
    refetch,
    error,
  };
}
